import { Grid } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import DirectionsRunIcon from "@material-ui/icons/DirectionsRun";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { maybe } from "@saleor/misc";
import { StoreOrderDetails_storeOrder_runner } from "@saleor/storeOrders/types/StoreOrderDetails";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";

import { SearchRunner_search_edges_node } from "../../types/SearchRunner";

export interface StoreOrderAssignRunnerDialogProps {
  runnerList: SearchRunner_search_edges_node[];
  fetchRunner: (query: string) => void;
  fetchMoreRunner: any;
  open: any;
  onClose: any;
  loading: boolean;
  onSubmit: any;
  runner: StoreOrderDetails_storeOrder_runner;
  orderMallId: string;
  setOrderMallId: React.Dispatch<React.SetStateAction<string>>;
}

interface RunnerInitialFormData {
  runnerId: string;
  runnerFirstName: string;
  runnerLastName: string;
  runnerContactNo: string;
}

const useStyles = makeStyles(
  () => ({
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    buttonActionFlex: {
      flexDirection: "column"
    },
    buttonActions: {
      display: "flex",
      width: "100%"
    },
    buttonWidth: {
      margin: "3px",
      width: "100%"
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colName: {
      paddingLeft: 0,
      width: "100%"
    },
    runnerDetailsContent: {
      alignItems: "center",
      display: "flex",
      fontSize: "17px",
      marginBottom: "6px"
    },
    sectionLabel: {
      margin: "0px",
      marginBottom: "6px",
      marginLeft: "5px"
    }
  }),
  { name: "StoreOrderAssignRunnerDialog" }
);

const StoreOrderAssignRunnerDialog: React.SFC<StoreOrderAssignRunnerDialogProps> = props => {
  const {
    onClose,
    open,
    fetchRunner,
    fetchMoreRunner,
    loading,
    runnerList,
    onSubmit,
    runner,
    orderMallId,
    setOrderMallId
  } = props;
  const classes = useStyles(props);
  const runnerInitialForm: RunnerInitialFormData = {
    runnerContactNo: maybe(() => runner.contactNo, null),
    runnerFirstName: maybe(() => runner.firstName, null),
    runnerId: maybe(() => runner.id, null),
    runnerLastName: maybe(() => runner.lastName, null)
  };
  const [runnerTempList, setRunnerTempList] = React.useState([]);
  const [runnerDisplayName, setRunnerDisplayName] = useStateFromProps(
    maybe(() => runner) ? `${runner?.firstName} ${runner?.lastName} ` : ""
  );

  React.useEffect(() => {
    setRunnerTempList(runnerList);
  }, [runnerList]);

  const runnerChoices = maybe(
    () =>
      runnerList.map(rider => ({
        label: `${rider.firstName} ${rider.lastName}`,
        value: rider?.id
      })),
    []
  );

  const getRunnerDetails = runnerId =>
    maybe(() => runnerTempList.find(e => e.id === runnerId));

  return (
    <Dialog onClose={onClose} open={open} maxWidth="xs" fullWidth>
      <Form
        initial={runnerInitialForm}
        onSubmit={e => {
          onSubmit(e.runnerId);
          onClose();
        }}
      >
        {({ data: formData, change, submit, reset }) => {
          const handleSelectRider = createSingleAutocompleteSelectHandler(
            change,
            setRunnerDisplayName,
            runnerChoices
          );

          return (
            <>
              <DialogTitle>Assign to Runner</DialogTitle>
              <DialogContent style={{ height: "43vh" }}>
                <h3 className={classes.sectionLabel}>Available Runners</h3>
                <SingleAutocompleteSelectField
                  label={formData.runnerId ? "" : "Select Runner"}
                  displayValue={runnerDisplayName}
                  disableOnBlur={true}
                  onChange={event => {
                    handleSelectRider(event);
                    change({
                      target: {
                        name: "runnerFirstName",
                        value: getRunnerDetails(event.target.value).firstName
                      }
                    });
                    change({
                      target: {
                        name: "runnerLastName",
                        value: getRunnerDetails(event.target.value).lastName
                      }
                    });
                    change({
                      target: {
                        name: "runnerContactNo",
                        value: getRunnerDetails(event.target.value).contactNo
                      }
                    });
                  }}
                  choices={runnerChoices}
                  name="runnerId"
                  value={formData.runnerId}
                  fetchChoices={fetchRunner}
                  {...fetchMoreRunner}
                  disabled={loading}
                  onFocus={() => {
                    if (orderMallId) {
                      setOrderMallId(orderMallId);
                    }
                  }}
                />
                <FormSpacer />
                <h3 className={classes.sectionLabel}>Runner Details</h3>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={6} md={6}>
                    <span className={classes.runnerDetailsContent}>
                      <DirectionsRunIcon
                        style={{
                          color: "#9B51E0",
                          fontSize: "33px",
                          marginRight: "10px"
                        }}
                      />
                      {formData.runnerFirstName
                        ? `${formData.runnerFirstName} ${formData.runnerLastName}`
                        : "--"}
                    </span>
                  </Grid>
                  <Grid item xs={6} sm={6} md={6}>
                    <span className={classes.runnerDetailsContent}>
                      <PhoneAndroidIcon
                        style={{
                          color: "#9B51E0",
                          fontSize: "27px",
                          marginLeft: "4px",
                          marginRight: "13px"
                        }}
                      />
                      {formData.runnerContactNo
                        ? formData.runnerContactNo
                        : "--"}
                    </span>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions className={classes.buttonActionFlex}>
                <div className={classes.buttonActions}>
                  <Button
                    onClick={onClose}
                    variant="contained"
                    color="default"
                    className={classes.buttonWidth}
                  >
                    BACK
                  </Button>
                  <Button
                    autoFocus
                    onClick={() => {
                      submit();
                      setRunnerDisplayName("");
                      reset();
                    }}
                    variant="contained"
                    color="primary"
                    disabled={!formData.runnerId}
                    className={classes.buttonWidth}
                  >
                    UPDATE
                  </Button>
                </div>
              </DialogActions>
            </>
          );
        }}
      </Form>
    </Dialog>
  );
};
StoreOrderAssignRunnerDialog.displayName = "StoreOrderAssignRunnerDialog";
export default StoreOrderAssignRunnerDialog;
